/* eslint-disable no-plusplus */
/* eslint-disable max-len */
import React, {
    useCallback, useContext, useEffect, useState
} from 'react';
import PropTypes from 'prop-types';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { TranslatorContext, FormattedMessage } from '@jutro/locale';
import { useAuthentication } from 'gw-digital-auth-react';
import { ApplicationHeader, DropdownMenuLink } from '@jutro/router';
import {
    withApplicationPropertyContext, EmailNotificationComponent
} from 'tdic-components-platform-react';
import { RiskManagementNotifications } from 'tdic-capability-risk-management-react';
import { BreakpointTrackerContext } from '@jutro/layout';
import { isCapabilityEnabled, getNormalizedLOBName } from 'gw-portals-config-js';
import { UsersProfileDetailsService } from 'gw-capability-profileinfo';
import { PolicyService } from 'gw-capability-policy';
import {
    ModalNextProvider, Loader, Link, InlineNotification
} from '@jutro/components';
import { error } from '@jutro/logger';

import metadata from './PageHeaderComponent.metadata.json5';
import pageHeaderStyles from './PageHeaderComponent.module.scss';
import messages from './PageHeaderComponent.messages';

function PageHeaderComponent(props) {
    const auth = useAuthentication();
    const {
        routes, location: { pathname }, isAuthorisedUser, history
    } = props;
    const translator = useContext(TranslatorContext);
    const { env, applicationDetails, tdic: { ignoreHeaderFooterPath } } = appConfig;
    const breakpoint = useContext(BreakpointTrackerContext);
    const { capabilitiesConfig } = appConfig;
    const faqLink = _.get(appConfig, `tdic.portalFAQURL.${_.toLower(process.env.NODE_ENV)}`, _.get(appConfig, 'tdic.portalFAQURL.development'));

    const [emailNotificationData, updateEmailNotificationData] = useState([]);
    const [renewalSurveyPolicy, updateRenewalSurveyPolicy] = useState(false);
    const [rmExpiringData, updateRMExpiringData] = useState([]);
    const [isLoading, setLoadingState] = useState(false);
    const [policyQuestionnaireData, setPolicyQuestionnaireData] = useState('');

    const updateNotificationData = useCallback((notification) => {
        const plPolicyChangeSurveyAvailable = _.get(_.find(notification, { menuOption: 'PLPolicyChangeSurvey' }), 'available', false);
        const policiesForAnestheticModalityAvailable = _.get(_.find(notification, { menuOption: 'PoliciesForAnestheticModalityQuestionnaire' }), 'available', false);
        const isPLSurveyAvailable = plPolicyChangeSurveyAvailable || policiesForAnestheticModalityAvailable;
        updateRenewalSurveyPolicy(isPLSurveyAvailable);
        if (isPLSurveyAvailable) {
            const policyQuestionnaires = _.find(notification, { menuOption: 'PoliciesForAnestheticModalityQuestionnaire', available: true });
            const values = _.get(policyQuestionnaires, 'values', []);
            const policyNumbers = Array.isArray(values) ? values.filter(Boolean) : [];
            setPolicyQuestionnaireData(policyNumbers[0]);
        }
        const riskManagementCourses = _.find(notification, { menuOption: 'ExpiringRiskManagementCourses' });
        if (_.get(riskManagementCourses, 'available', false)) {
            const courseExpireData = _.get(riskManagementCourses, 'values', []);
            updateRMExpiringData(courseExpireData);
        }
        const primaryNameData = _.find(notification, { menuOption: 'PrimaryNamedInsuredWithEmptyEmail' });
        if (_.get(primaryNameData, 'available', false)) {
            const primaryInsuredEmail = _.get(primaryNameData, 'values', []);
            updateEmailNotificationData(primaryInsuredEmail);
        }
    }, []);

    useEffect(() => {
        if (!isAuthorisedUser) {
            setLoadingState(true);
            UsersProfileDetailsService.getUserNotifications(auth.authHeader)
                .then((response) => {
                    updateNotificationData(response);
                })
                .catch((err) => {
                    error(`PageHeaderComponent: getUserNotifications - Unable to get user notifications ${JSON.stringify(err)}`);
                })
                .finally(() => {
                    setLoadingState(false);
                });
        }
    }, [auth.authHeader, isAuthorisedUser, updateNotificationData]);

    const doLogout = useCallback(() => {
        auth.tdicLogout();
    }, [auth]);

    const getRoutes = useCallback(() => {
        return routes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [breakpoint]);

    const showBillingSection = useCallback(() => {
        return isCapabilityEnabled({ capabilitiesConfig, capabilityName: 'billing' });
    }, [capabilitiesConfig]);

    const getAvatarLinks = useCallback(() => {
        const links = [];
        links.push([
            <DropdownMenuLink to="/userprofile">
                {breakpoint === 'desktop' ? translator(messages.account) : translator(messages.myAccountDetails)}
            </DropdownMenuLink>
        ]);

        if (!isAuthorisedUser) {
            links.push([
                <DropdownMenuLink to="/user-management">
                    {translator(messages.authorizedUser)}
                </DropdownMenuLink>,
            ]);
        }

        if (breakpoint !== 'desktop') {
            if (showBillingSection) {
                links.push([
                    <DropdownMenuLink to="/make-payment">
                        {translator(messages.payMyBill)}
                    </DropdownMenuLink>
                ]);
            }
            if (!isAuthorisedUser) {
                links.push([
                    <DropdownMenuLink to="/billing-summary">
                        {translator(messages.managePaymentPlan)}
                    </DropdownMenuLink>
                ]);

                links.push([
                    <DropdownMenuLink to="/request-a-quote">
                        {translator(messages.requestQuote)}
                    </DropdownMenuLink>
                ]);
            }
            links.push([
                <DropdownMenuLink href={faqLink} target="_blank">
                    {translator(messages.faq)}
                </DropdownMenuLink>
            ]);
        }
        if (!auth.useJutroAuthContext) {
            links.push([
                <DropdownMenuLink onClick={doLogout}>
                    {translator(messages.logout)}
                </DropdownMenuLink>
            ]);
        }
        return links;
    // eslint-disable-next-line no-use-before-define
    }, [translator, auth.useJutroAuthContext, doLogout, isAuthorisedUser, breakpoint, faqLink, showBillingSection]);

    const getLogoSrc = useCallback(() => {
        const url = _.get(env, 'DEPLOYMENT_URL');
        const { applicationLogo } = applicationDetails;
        return `${url}/${applicationLogo}`;
    }, [applicationDetails, env]);

    const redirectToProfileChangeEndorsement = useCallback(
        (event, policyDetails, requestType, policyNumber) => {
            event.preventDefault();
            const productCode = policyDetails.currentPeriod.offeringCode;
            const lobName = getNormalizedLOBName(productCode);
            history.push(`/endorsement-${lobName}/profile-change`, {
                policyNumber: policyNumber,
                policyDetails: policyDetails,
                requestType: 'profileChange'
            });
        },
        [history]
    );

    const getPolicyQuestionnaireMessage = useCallback(() => {
        return (
            <FormattedMessage
                {...messages.renewalSurveyNotify}
                values={{
                    date: policyQuestionnaireData.split('-')[2],
                    policyQuestionnaireLink: <Link to="/pl-policy-questionnaire">{translator(messages.policyQuestionnaire)}</Link>
                }}
            />
        );
    }, [policyQuestionnaireData, translator]);

    const onPolicyClick = useCallback(
        async (event, policyNumber) => {
            event.preventDefault();
            setLoadingState(true);
            try {
                const policyDetails = await PolicyService.getAccountPolicyDetails(
                    policyNumber,
                    auth.authHeader
                );
                redirectToProfileChangeEndorsement(
                    event,
                    policyDetails,
                    'policyChange',
                    policyNumber
                );
            } catch (err) {
                error(
                    `EmailNotification: onPolicyClick - Unable to get policy details ${JSON.stringify(err)}`
                );
                ModalNextProvider.showAlert({
                    title: messages.policyRequestFailed,
                    message: messages.CanNotLoadYourPolicyAtThisTime,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: messages.ok
                }).then(() => history.push('/error'));
            } finally {
                setLoadingState(false);
            }
        },
        [auth.authHeader, history, redirectToProfileChangeEndorsement]
    );

    const getNotifications = useCallback(() => {
        const hasEmailNotification = !_.isEmpty(emailNotificationData);
        const hasRMNotification = !_.isEmpty(rmExpiringData);
        const hasSurveyNotification = renewalSurveyPolicy && !isAuthorisedUser;

        if (!hasEmailNotification && !hasRMNotification && !hasSurveyNotification) {
            return (
                <div className={pageHeaderStyles.noNotifications}>
                    {translator(messages.noNewNotification)}
                </div>
            );
        }

        return (
            <div className={pageHeaderStyles.notificationDropdown}>
                {hasEmailNotification && (
                    <div className={pageHeaderStyles.notificationItem}>
                        <EmailNotificationComponent
                            notifications={emailNotificationData}
                            onPolicyClick={onPolicyClick}
                            visible={hasEmailNotification}
                        />
                    </div>
                )}
                {hasRMNotification && (
                    <div className={pageHeaderStyles.notificationItem}>
                        <RiskManagementNotifications notifications={rmExpiringData} />
                    </div>
                )}
                {hasSurveyNotification && (
                    <div className={pageHeaderStyles.notificationItem}>
                        <InlineNotification
                            message={getPolicyQuestionnaireMessage()}
                            type="info"
                            isDismissable={false}
                            className={pageHeaderStyles.inlineNotificationStyle}
                        />
                    </div>
                )}
            </div>
        );
    }, [emailNotificationData, getPolicyQuestionnaireMessage, isAuthorisedUser, onPolicyClick, renewalSurveyPolicy, rmExpiringData, translator]);

    const totalNotifications = (() => {
        let count = 0;

        // EMAIL: Always count as 1 if ANY email notifications exist (not array length)
        if (!_.isEmpty(emailNotificationData)) {
            count += 1; // Single email notification regardless of array size
        }

        // RM: Count actual number of valid items in array
        if (!_.isEmpty(rmExpiringData) && Array.isArray(rmExpiringData)) {
            const validRMNotifications = rmExpiringData.filter((item) => item && !_.isEmpty(item));
            count += validRMNotifications.length; // Multiple RM notifications
        } else if (!_.isEmpty(rmExpiringData)) {
            count += 1; // Single RM notification
        }

        // Survey: Count as 1 if active
        if (renewalSurveyPolicy && !isAuthorisedUser) {
            count += 1;
        }

        return count;
    })();

    const overrides = {
        applicationheader: {
            avatarProps: {
                username: auth.name,
                title: auth.name,
                subtitle: auth.user_name
            },
            avatarChildren: getAvatarLinks(),
            logoSrc: getLogoSrc(),
            navigationRoutes: getRoutes(),
            notificationChildren: getNotifications()
        },
        notificationBadge: {
            'data-count': String(totalNotifications)
        }
    };

    const resolvers = {
        resolveClassNameMap: pageHeaderStyles,
        resolveComponentMap: {
            applicationheader: ApplicationHeader,
            emailnotificationcomponent: EmailNotificationComponent
        }
    };

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    if (_.includes(ignoreHeaderFooterPath, pathname)) {
        return null;
    }
    return renderContentFromMetadata(metadata.componentContent, overrides, resolvers);
}

PageHeaderComponent.propTypes = {
    routes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    isAuthorisedUser: PropTypes.bool.isRequired
};

export default withRouter(withApplicationPropertyContext(PageHeaderComponent));
